import { createSlice } from '@reduxjs/toolkit';
import { clearState } from '../../rootAccess';

const initialState = {
   drafts: {}, // { [jid]: { message: '', replyTo: '', editMessageId: '', fileIds: [], activeFileId: null, mediaType: null, fileMetadata: {} } }
};

const ensureDraft = (state, jid, defaults = {}) => {
   if (!state.drafts[jid]) state.drafts[jid] = defaults;
   return state.drafts[jid];
};

const clearMediaFields = draft => {
   delete draft.fileIds;
   delete draft.mediaType;
   delete draft.activeFileId;
   delete draft.fileMetadata;
   delete draft.showPreview;
};

const draftSlice = createSlice({
   name: 'draftData',
   initialState,
   reducers: {
      setDraft: (state, action) => {
         const { jid, message } = action.payload;
         if (!message || message.trim() === '') {
            if (state.drafts[jid]) {
               delete state.drafts[jid].message;
               // Clean up if no other data exists
               const draft = state.drafts[jid];
               if (!draft.fileIds?.length && !draft.replyTo && !draft.editMessageId) {
                  delete state.drafts[jid];
               }
            }
         } else {
            if (!state.drafts[jid]) {
               state.drafts[jid] = {};
            }
            state.drafts[jid].message = message;
         }
      },
      clearDraft: (state, action) => {
         const jid = action.payload;
         delete state.drafts[jid];
      },
      setReplyTo: (state, action) => {
         const { jid, msgId } = action.payload;
         if (!state.drafts[jid]) {
            state.drafts[jid] = {};
         }
         state.drafts[jid].replyTo = msgId;
      },
      clearReplyTo: (state, action) => {
         const jid = action.payload;
         if (state.drafts[jid]) {
            delete state.drafts[jid].replyTo;
         }
      },
      setEditMessage: (state, action) => {
         const { jid, msgId } = action.payload;
         if (!state.drafts[jid]) {
            state.drafts[jid] = {};
         }
         state.drafts[jid].editMessageId = msgId;
      },
      clearEditMessage: (state, action) => {
         const jid = action.payload;
         if (state.drafts[jid]) {
            delete state.drafts[jid].editMessageId;
         }
      },
      resetDrafts: () => initialState,
      // Media preview reducers
      setMediaFiles: (state, action) => {
         const { jid, fileIds, fileMetadata, mediaType, showPreview = true } = action.payload;
         const draft = ensureDraft(state, jid);
         draft.fileIds = fileIds;
         draft.fileMetadata = fileMetadata;
         draft.mediaType = mediaType;
         draft.activeFileId = fileIds[0] ?? null;
         draft.showPreview = showPreview;
      },
      addMediaFiles: (state, action) => {
         const { jid, fileIds, fileMetadata } = action.payload;
         const draft = ensureDraft(state, jid, { fileIds: [], fileMetadata: {} });
         draft.fileIds ??= [];
         draft.fileMetadata ??= {};
         draft.fileIds.push(...fileIds);
         Object.assign(draft.fileMetadata, fileMetadata);
         draft.activeFileId = fileIds[fileIds.length - 1] ?? null;
      },
      removeMediaFile: (state, action) => {
         const { jid, fileId } = action.payload;
         const draft = state.drafts[jid];
         if (!draft?.fileIds) return;

         const currentIndex = draft.fileIds.indexOf(fileId);
         draft.fileIds = draft.fileIds.filter(id => id !== fileId);
         delete draft.fileMetadata?.[fileId];

         if (draft.fileIds.length === 0) {
            clearMediaFields(draft);
         } else if (draft.activeFileId === fileId) {
            draft.activeFileId = draft.fileIds[Math.max(0, currentIndex - 1)] ?? null;
         }
      },
      updateFileMetadata: (state, action) => {
         const { jid, fileId, metadata } = action.payload;
         const fileMetadata = state.drafts[jid]?.fileMetadata;
         if (!fileMetadata) return;
         fileMetadata[fileId] = { ...fileMetadata[fileId], ...metadata };
      },
      setActiveFileId: (state, action) => {
         const { jid, fileId } = action.payload;
         if (state.drafts[jid]) state.drafts[jid].activeFileId = fileId;
      },
      clearMediaPreview: (state, action) => {
         const jid = action.payload;
         const draft = state.drafts[jid];
         if (!draft) return;
         clearMediaFields(draft);
         delete draft.replyTo;
         if (!draft.message) delete state.drafts[jid];
      },
      setIsProcessingMedia: (state, action) => {
         const { jid, isProcessing } = action.payload;
         if (!state.drafts[jid]) state.drafts[jid] = {};
         state.drafts[jid].isProcessingMedia = isProcessing;
      },
   },
   extraReducers: builder => {
      builder.addCase(clearState, () => initialState);
   },
});

export const {
   setDraft,
   clearDraft,
   resetDrafts,
   setMediaFiles,
   addMediaFiles,
   removeMediaFile,
   updateFileMetadata,
   setActiveFileId,
   clearMediaPreview,
   setReplyTo,
   clearReplyTo,
   setEditMessage,
   clearEditMessage,
   setIsProcessingMedia,
} = draftSlice.actions;

export const draftReducer = draftSlice.reducer;
